import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchChannelAccounts, fetchOperadorChannelAccounts, setOperadorChannelAccounts } from '../api'
import { Button } from './ui/button'
import { Label } from './ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'

interface OperatorTarget {
  user_id: string
  nombre: string
}

export function OperatorAssignmentsDialog({
  member,
  sociedadId,
  onClose,
}: {
  member: OperatorTarget | null
  sociedadId: string | null
  onClose: () => void
}) {
  const open = !!member && !!sociedadId
  const [channelIds, setChannelIds] = useState<string[]>([])

  const channelAccountsQuery = useQuery({
    queryKey: ['channel-accounts', sociedadId],
    queryFn: () => fetchChannelAccounts(sociedadId!),
    enabled: open,
  })
  const assignedChannelsQuery = useQuery({
    queryKey: ['operador-channel-accounts', sociedadId, member?.user_id],
    queryFn: () => fetchOperadorChannelAccounts(sociedadId!, member!.user_id),
    enabled: open,
  })

  useEffect(() => {
    if (assignedChannelsQuery.data) setChannelIds(assignedChannelsQuery.data)
  }, [assignedChannelsQuery.data])

  const saveMutation = useMutation({
    mutationFn: () => setOperadorChannelAccounts(sociedadId!, member!.user_id, channelIds),
    onSuccess: () => {
      toast.success(`Actualizamos las asignaciones de ${member?.nombre}.`)
      onClose()
    },
    onError: () => toast.error('No se pudieron guardar las asignaciones.'),
  })

  function toggle(list: string[], setList: (v: string[]) => void, id: string): void {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Asignar números a {member?.nombre}</DialogTitle>
          <DialogDescription>
            Este Operador solo va a ver y poder responder conversaciones que usen uno de estos números de trabajo,
            con cualquier contacto. La IA sigue respondiendo igual en toda la Sociedad, esto solo afecta lo que ve en
            el dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div>
            <Label className="mb-2 block">Números de trabajo (WhatsApp)</Label>
            <div className="flex max-h-40 flex-col gap-1.5 overflow-y-auto rounded-md border p-2">
              {channelAccountsQuery.data?.length === 0 && (
                <p className="p-2 text-sm text-muted-foreground">No hay números de WhatsApp todavía.</p>
              )}
              {channelAccountsQuery.data?.map((ca) => (
                <label key={ca.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
                  <input
                    type="checkbox"
                    checked={channelIds.includes(ca.id)}
                    onChange={() => toggle(channelIds, setChannelIds, ca.id)}
                  />
                  {ca.display_name || ca.phone_number}
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            Guardar asignaciones
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
